import { Trans } from '@lingui/macro';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
    useSelectorCurrentProductId,
    useSelectorProfileLocale
} from '../../../../app';
import { handleOpenDucsEventModal } from '../../../../app/services/hooks/use-portal-communication-service';
import {
    useSelectorModuleIdsForNewBadgeStyling,
    useSelectorNumeracyForEarlyLearnersModuleId
} from '../../../../configuration/configuration.selectors';
import {
    useSelectedModuleArrayById,
    useSelectorIsInternalUsers
} from '../../../../user-profiles';
import { ROUTES } from '../../../index';
import {
    LandingBadge,
    LandingBadgeVariants
} from '../landing-badge';
import {
    BadgeContainer,
    ButtonWrapper,
    CompareTheseFeatures,
    Content
} from './landing-content.styled';
import {
    LandingContentProps,
    LandingBadgeOpenOptions,
    BadgeVisibilityOptions
} from './landing-content.types';
import {
    TeacherAdminIcon,
    TeacherAdminIcons
} from '@components';

/**
 * Container that holds landing badges
 * Route initiated when clicked left nav item has > 1 modules
 * Gets the selected module values of left nav item
 * Manages different window events when badge is clicked
 * Passes all relevant info to the badge itself.
 */
export const LandingContent = ({ selectedModuleIds }: LandingContentProps) => {
    const history = useHistory();
    const dispatch = useDispatch();
    const productId = useSelectorCurrentProductId();
    const NumeracyForEarlyLearnersModuleId= useSelectorNumeracyForEarlyLearnersModuleId();
    const moduleIdsForNewBadgeStyling= useSelectorModuleIdsForNewBadgeStyling().split(',');
    const { locale } = useSelectorProfileLocale();
    const selectedModuleValues = useSelectedModuleArrayById(selectedModuleIds || []);
    const isInternalUsers = useSelectorIsInternalUsers();
    const handleLandingBadgeOpen = ({
        target,
        moduleLink,
        moduleId
    }: LandingBadgeOpenOptions) => {
        switch (target) {
        case '_self': {
            history.push(`${ROUTES.content}/${moduleId}`);
            break;
        }
        case '_top':
        case '_blank':{
            window.open(moduleLink, target);
            break;
        }
        default: break;
        }
    };
    const isBadgeVisible = ({
        productIds,
        isHidden
    }: BadgeVisibilityOptions) => {
        if (isHidden) return false;

        return isInternalUsers || productIds.includes(productId);
    };
    const girlId = selectedModuleValues[0]?.groupInfo || '';
    const handleCompareFeaturesClick = () => {
        const arrayGirlId: Array<string> = [girlId];

        handleOpenDucsEventModal({
            dispatch,
            message: arrayGirlId
        });
    };

    return (
        <Content>
            {girlId && <ButtonWrapper>
                <CompareTheseFeatures
                    colorScheme={'blue'}
                    data-testid={'compare-these-features'}
                    onClick={handleCompareFeaturesClick}
                    leftIcon={(
                        <TeacherAdminIcon
                            icon={'compare'}
                            h={22}
                        />
                    )}
                >
                    <Trans id={'compareFeatures'} />
                </CompareTheseFeatures>
            </ButtonWrapper>}
            <BadgeContainer>
                {productId &&
                selectedModuleValues &&
                selectedModuleValues.map(({
                    name,
                    description,
                    icon,
                    isHidden,
                    target,
                    moduleLink,
                    tooltip,
                    id,
                    productIds
                }) => {
                    if (isBadgeVisible({
                        isHidden,
                        productIds
                    })) {
                        // Hide NumeracyForEarlyLearnersModule when Spanish locale is selected
                        if (locale === 'es-MX' && id === NumeracyForEarlyLearnersModuleId) {
                            return null;
                        }
                        let variant = LandingBadgeVariants.default;

                        if (moduleIdsForNewBadgeStyling.includes(id.toString())) variant = LandingBadgeVariants.new;

                        return (
                            <LandingBadge
                                key={id}
                                id={id}
                                name={name}
                                description={description}
                                icon={icon as TeacherAdminIcons}
                                tooltip={tooltip}
                                onLandingBadgeOpen={() => handleLandingBadgeOpen({
                                    target,
                                    moduleLink,
                                    moduleId: id
                                })}
                                variant={variant}
                                locale={locale || 'en-AU'}
                            />
                        );
                    }

                    return null;
                })}
            </BadgeContainer>
        </Content>
    );
};
